import React, { useState, useRef, useCallback, useEffect } from 'react';
import '../css/WorkflowNode.css';
import axios from 'axios';
import BASE_URL from './URL';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FaSpinner } from 'react-icons/fa';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) throw new Error('API base URL not set. Please set REACT_APP_API_BASE_URL in .env');
const api = axios.create({ baseURL: 'https://django-backend-115705416374.asia-south1.run.app' });
// const api = axios.create({ baseURL: 'http://localhost:8000' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  res => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refresh = localStorage.getItem('refresh');
      if (refresh) {
        try {
          const res = await axios.post('https://django-backend-115705416374.asia-south1.run.app/api/token/refresh/', { refresh });
          // const res = await axios.post('http://localhost:8000/api/token/refresh/', { refresh });
          localStorage.setItem('access', res.data.access);
          originalRequest.headers.Authorization = `Bearer ${res.data.access}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          window.location.reload();
        }
      }
    }
    return Promise.reject(error);
  }
);
const isValidEmail = (email) => {
  if (!email || email.trim() === '') return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// --- SVG Icons ---
const Plus = ({ size = 24, className = "", onClick, title }) => (
  <svg onClick={onClick} xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}
    title={title} style={{ cursor: 'pointer' }}>
    <path d="M5 12h14" /><path d="M12 5v14" />
  </svg>
);

const Trash2 = ({ size = 24, className = "", onClick, title }) => (
  <svg onClick={onClick} xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}
    title={title} style={{ cursor: 'pointer' }}>
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

// --- Confirmation Modal ---
const ConfirmationModal = ({ company, onConfirm, isUpdate = false }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <h3 className="modal-header">Confirm {isUpdate ? 'Update' : 'Submission'}</h3>
      <p className="modal-body">
        Are you sure you want to {isUpdate ? 'update' : 'submit'} the workflow for{" "}
        <span className="text-indigo-600 font-semibold">{company}</span>?
        {isUpdate && <span className="text-orange-600 block mt-2">⚠️ This will replace the existing workflow.</span>}
      </p>
      <div className="modal-actions">
        <button onClick={() => onConfirm(false)} className="btn-secondary">Cancel</button>
        <button onClick={() => onConfirm(true)} className="btn-submit">
          Confirm & {isUpdate ? 'Update' : 'Submit'}
        </button>
      </div>
    </div>
  </div>
);

// Define the maximum number of approvers (emails) allowed per node.
// Default (NONE) is 1. AND/OR start with 2, can go up to 3.
const MAX_APPROVERS = 3;

// --- Workflow Node ---
const WorkflowNode = ({ node, index, onChange, onRemove, isLast, errors = {}, disabled = false }) => {

  const handleInputChange = (field, value) => {
    let newNode = { ...node, [field]: value };

    // when logic changes
    if (field === 'logic') {
      if (value === 'NONE') {
        // Ensure only one email is present for NONE logic
        newNode.emails = [newNode.emails[0] || ''];
      } else if ((value === 'AND' || value === 'OR') && newNode.emails.length < 2) {
        // Ensure at least two emails are present for AND/OR logic
        newNode.emails = [...newNode.emails, ''];
      }
    }
    onChange(newNode);
  };

  const handleEmailChange = (emailIndex, value) => {
    const newEmails = [...node.emails];
    newEmails[emailIndex] = value;
    onChange({ ...node, emails: newEmails });
  };

  // ➕ Add button logic — new email at end (with limit check)
  const handleAddEmail = () => {
    // 👇 MODIFICATION 1: Check the MAX_APPROVERS limit before adding a new email
    if (node.emails.length < MAX_APPROVERS) {
      onChange({ ...node, emails: [...node.emails, ''] });
    }
  };

  // 🗑️ Remove email input
  // 🗑️ Remove email input
  const handleRemoveEmail = (emailIndex) => {
    const newEmails = node.emails.filter((_, i) => i !== emailIndex);
    // Ensure there is always at least one email input field for NONE, or at least two for AND/OR
    if (node.logic === 'NONE' && newEmails.length === 0) {
      newEmails.push('');
    } else if ((node.logic === 'AND' || node.logic === 'OR') && newEmails.length < 2) {
      // If removing an email would leave fewer than 2 for AND/OR, add a blank one if needed, but not more than 2 total initially.
      // Or, just don't allow removing if it would drop below 2.
      if (emailIndex === 0 && newEmails.length === 1 && node.emails.length > 1) { // If trying to remove primary and only 1 secondary exists
        // Don't remove primary if only one other exists, shift that to primary
        newEmails[0] = node.emails[1];
      } else if (newEmails.length === 0) {
        newEmails.push(''); // Ensure at least one primary
        if (node.logic !== 'NONE') newEmails.push(''); // And a secondary for AND/OR
      }
    }
    onChange({ ...node, emails: newEmails });
  };



  // UI logic setup
  const showMultipleInputs = node.logic === 'AND' || node.logic === 'OR';
  // 👇 New variable to check if the user can add more
  const canAddMore = node.emails.length < MAX_APPROVERS;

  let nodeClass = `node-card node-${node.logic.toLowerCase()}`;
  let message = "*Select 'AND' for sequential approval, or 'OR' for alternate approvers.";

  const getLogicMessage = () => {
    if (node.logic === 'OR') {
      return <p className="logic-message logic-message-or">*In 'OR' logic, either the Primary or Secondary Approver can complete this step. (Alternate Approver)</p>;
    } else if (node.logic === 'AND') {
      return <p className="logic-message logic-message-and">*In 'AND' logic, approval is sequential, and the secondary email can act as a backup contact or parallel approver if needed.</p>;
    }
    return null; // For 'NONE' logic, no specific message in the image
  };

  return (
    <div className={`timeline-step-wrapper ${isLast ? 'last-step' : ''}`}>
      {/* Timeline Indicator */}
      <div className="timeline-indicator">
        <div className="timeline-number">{index + 1}</div>
        {!isLast && <div className="timeline-line"></div>} {/* Vertical Line */}
      </div>

      {/* Node Card */}
      <div className="node-card">
        <div className="node-header">
          <h4 className="node-title">Approval Step {index + 1}</h4>
          <button
            onClick={() => onRemove(index)}
            className="node-remove-btn"
            title="Remove Node"
            disabled={disabled}
            style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <Trash2 size={20} className="icon-trash" />
          </button>
        </div>

        <div className="node-inputs-container">
          <input
            type="text"
            value={node.name || ''}
            placeholder="Approval Name (e.g., Finance Manager)"
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`input-field ${errors.name ? 'input-error' : ''}`}
            disabled={disabled}
          />
          {errors.name && <p className="error-text">{errors.name}</p>}

          {/* Emails Section */}
          {node.emails.map((email, emailIndex) => (
            <div key={emailIndex} className="node-email-group">
              {/* Logic Dropdown (Only for the first email input) */}
              {emailIndex === 0 && (
                <select
                  value={node.logic}
                  onChange={(e) => handleInputChange('logic', e.target.value)}
                  className="logic-select"
                  title="Approval Logic"
                  disabled={disabled}
                >
                  <option value="NONE">NONE</option>
                  <option value="OR">OR(Parallel / Alternate)</option>
                  <option value="AND">AND (Sequential)</option>
                </select>
              )}

              {/* Logic Separator (For subsequent emails in AND/OR mode) */}
              {emailIndex > 0 && showMultipleInputs && (
                <span className={`logic-separator logic-${node.logic.toLowerCase()}`}>{node.logic}</span>
              )}

              {/* Email Input Field */}
              <input
                type="email"
                value={email || ''}
                placeholder={
                  emailIndex === 0
                    ? "Primary Approver Email"
                    : "Secondary Approver Email (Alternate)"
                }
                onChange={(e) => handleEmailChange(emailIndex, e.target.value)}
                className={`input-field ${errors.emails && errors.emails[emailIndex] ? 'input-error' : ''}`}
                disabled={disabled}
              />

              {/* Add Button */}
              {showMultipleInputs && emailIndex === node.emails.length - 1 && canAddMore && (
                <Plus
                  size={24}
                  onClick={disabled ? undefined : handleAddEmail}
                  className={`add-email-btn`}
                  title="Add Another Approver"
                  style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                />
              )}
              {/* Remove Button (for secondary/tertiary emails) */}
              {showMultipleInputs && emailIndex > 0 && (node.emails.length > 1 || (node.logic === 'NONE' && node.emails.length === 1 && emailIndex === 0)) && (
                <Trash2
                  size={24}
                  onClick={disabled ? undefined : () => handleRemoveEmail(emailIndex)}
                  className="remove-email-btn"
                  title="Remove Approver"
                  style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                />
              )}
            </div>
          ))}

          {/* Email validation errors */}
          {node.emails.map((_, emailIndex) => (
            errors.emails && errors.emails[emailIndex] && (
              <p key={`err-${index}-${emailIndex}`} className="error-text email-error-specific">{errors.emails[emailIndex]}</p>
            )
          ))}

          {/* Logic Message/Hint */}
          {getLogicMessage()}
        </div>
      </div>
    </div>
  );
};

// --- Main App Component ---
const initialNodes = [
  { name: 'Department Head', emails: ['head@example.com'], logic: 'NONE' },
  { name: 'VP of Operations', emails: ['vp_ops@example.com'], logic: 'AND' }
];

export default function App() {
  const [workflow, setWorkflow] = useState(initialNodes);
  const [company, setCompany] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [companies, setCompanies] = useState(['STL - Digital', 'ONB', 'Invenia']);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyDescription, setNewCompanyDescription] = useState('');
  const [newCompanyLogo, setNewCompanyLogo] = useState(null); // logo file
  const [newCompanyScmHead, setNewCompanyScmHead] = useState(''); // SCM-HEAD email
  const [newCompanyTerms, setNewCompanyTerms] = useState(''); // T&C
  const [isUpdatingWorkflow, setIsUpdatingWorkflow] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const fileInputRef = useRef(); // add near the top of App
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);

  // Terms and Conditions modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedCompanyForTerms, setSelectedCompanyForTerms] = useState('');
  const [companyTerms, setCompanyTerms] = useState('');
  const [isEditingTerms, setIsEditingTerms] = useState(false);
  const [originalTerms, setOriginalTerms] = useState('');
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [isSavingTerms, setIsSavingTerms] = useState(false);

  const handleCreateCompany = async () => {
    setLogoError(false);

    // 🆕 Validation with pop-up alerts (similar to registration form)
    if (!newCompanyName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Company Name Required',
        text: 'Please enter a company name before creating the company.',
        confirmButtonColor: '#667eea',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!newCompanyLogo) {
      setLogoError(true);
      Swal.fire({
        icon: 'warning',
        title: 'Logo Required',
        text: 'Please select a logo file before creating the company.',
        confirmButtonColor: '#667eea',
        confirmButtonText: 'OK'
      });
      return;
    }

    setIsCreatingCompany(true);
    try {
      const formData = new FormData();
      formData.append('name', String(newCompanyName || '').trim());
      formData.append('description', String(newCompanyDescription || '').trim());
      formData.append('logo', newCompanyLogo);
      formData.append('scm_head', String(newCompanyScmHead || '').trim());
      formData.append('terms', String(newCompanyTerms || '').trim());
      const res = await api.post('/api/companies/create/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCompanies(prev => [...prev, res.data.name]);
      setCompany(res.data.name);
      setShowAddCompany(false);
      setNewCompanyName('');
      setNewCompanyDescription('');
      setNewCompanyLogo(null);
      setNewCompanyScmHead('');
      setNewCompanyTerms('');
      setMessage(`Company "${res.data.name}" created successfully!`);
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage(`Failed to create company: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setIsCreatingCompany(false);
    }
  };

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/api/companies/');
      const companyNames = res.data.map(company => company.name);
      setCompanies(companyNames);
    } catch (err) {
      // Failed to fetch companies
      // Keep default companies if API fails
    }
  }, []);

  // Load companies on component mount
  React.useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Fetch permissions; if not super admin, lock to effective company and pre-load
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/user-permissions/');
        const data = res.data || {};
        setIsSuperAdmin(!!data.isSuperAdmin);
        if (!data.isSuperAdmin && data.effectiveCompany) {
          const c = String(data.effectiveCompany || '').trim();
          if (c) {
            setCompany(c);
            // ensure company exists in the options list
            setCompanies(prev => (prev.includes(c) ? prev : [...prev, c]));
          }
        }
      } catch { }
    })();
  }, []);

  // Load workflow when company changes
  const loadWorkflowForCompany = useCallback(async (companyName) => {
    try {
      const cname = String(companyName || '').trim();
      if (!cname) return;
      const res = await api.get(`/api/viewworkflow/?company=${encodeURIComponent(cname)}`);
      if (res.data.workflow && res.data.workflow.length > 0) {
        // Convert backend format to frontend format
        const frontendWorkflow = res.data.workflow.map(node => ({
          name: node.approval_name,
          emails: [
            node.approval_email || '',
            node.link_email || '',
            node.link_email_2 || ''
          ].filter(email => email.trim() !== ''), // Remove empty emails
          logic: node.logic || 'NONE'
        }));
        setWorkflow(frontendWorkflow);
        setIsUpdatingWorkflow(true); // Mark as updating existing workflow
        setMessage(`Loaded existing workflow for ${cname}`);
        setTimeout(() => setMessage(null), 3000);
      } else {
        // No existing workflow, reset to default
        setWorkflow(initialNodes);
        setIsUpdatingWorkflow(false); // Mark as creating new workflow
      }
    } catch (err) {
      // Failed to load workflow
      // Keep current workflow on error
    }
  }, []);

  // Load workflow when company selection changes
  React.useEffect(() => {
    if (company) {
      loadWorkflowForCompany(company);
    }
  }, [company, loadWorkflowForCompany]);

  const validateWorkflow = useCallback(() => {
    const errors = {};
    let isValid = true;

    workflow.forEach((node, index) => {
      errors[index] = {};
      if (!node.name || !node.name.trim()) {
        errors[index].name = "Approval Name is required.";
        isValid = false;
      }

      const emailErrors = [];
      if (node.emails.length === 0 || !node.emails[0].trim()) {
        emailErrors[0] = "Primary email is required.";
        isValid = false;
      }

      node.emails.forEach((email, emailIndex) => {
        if (email.trim() && !isValidEmail(email)) {
          emailErrors[emailIndex] = "Invalid email format.";
          isValid = false;
        }
      });

      // Enforce MAX_APPROVERS limit during validation as a safeguard
      if (node.emails.length > MAX_APPROVERS) {
        isValid = false;
        // Note: The UI now prevents this, but a backend check is always wise.
      }

      if (emailErrors.length > 0) errors[index].emails = emailErrors;
    });

    setValidationErrors(errors);
    return isValid;
  }, [workflow]);

  const handleAddStep = () => {
    const newNode = { name: '', emails: [''], logic: 'NONE' };
    setWorkflow(prev => [...prev, newNode]);
  };

  const handleNodeChange = (index, updatedNode) => {
    setWorkflow(prev => prev.map((node, i) => (i === index ? updatedNode : node)));
  };

  const removeNode = (indexToRemove) => {
    setWorkflow(prev => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async () => {
    if (!validateWorkflow()) {
      setMessage("Validation failed! Please fix the errors below.");
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async (isConfirmed) => {
    setShowConfirm(false);
    if (!isConfirmed) return;

    setIsSavingWorkflow(true);
    setMessage(isUpdatingWorkflow ? 'Updating workflow...' : 'Creating workflow...');
    try {
      const res = await api.post('/api/workflow/', { company, nodes: workflow });
      if (res.status === 201 || res.status === 200) {
        setMessage(`Workflow for ${company} ${isUpdatingWorkflow ? 'updated' : 'created'} successfully!`);
        // Reload the workflow to reflect any changes
        loadWorkflowForCompany(company);
      } else {
        setMessage(`Error ${isUpdatingWorkflow ? 'updating' : 'creating'} workflow.`);
      }
    } catch (err) {
      // Error occurred during workflow operation
      setMessage(`Submission failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsSavingWorkflow(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleReset = () => {
    // Reset to default nodes or reload existing workflow for current company
    if (company) {
      loadWorkflowForCompany(company);
    } else {
      setWorkflow(initialNodes);
    }
    setValidationErrors({});
    setMessage('Workflow reset.');
    setTimeout(() => setMessage(null), 3000);
  };

  // Terms and Conditions handlers
  const handleViewTerms = async () => {
    if (!company) {
      setMessage('Please select a company first');
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSelectedCompanyForTerms(company);
    setIsLoadingTerms(true);
    setShowTermsModal(true);

    try {
      const response = await api.get('/api/company-terms/', {
        params: { company: company }
      });

      const terms = response.data.terms || '';
      setCompanyTerms(terms);
      setOriginalTerms(terms);
      setIsEditingTerms(false);
    } catch (err) {
      setMessage(`Failed to load terms: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setMessage(null), 4000);
      setCompanyTerms('');
      setOriginalTerms('');
    } finally {
      setIsLoadingTerms(false);
    }
  };

  const handleEditTerms = () => {
    setIsEditingTerms(true);
  };

  const handleSaveTerms = async () => {
    setIsSavingTerms(true);
    try {
      await api.put('/api/company-terms/update/', {
        company_name: selectedCompanyForTerms,
        terms: companyTerms
      });

      setOriginalTerms(companyTerms);
      setIsEditingTerms(false);
      setMessage('Terms and conditions updated successfully');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage(`Failed to save terms: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleCancelTerms = () => {
    setCompanyTerms(originalTerms);
    setIsEditingTerms(false);
  };

  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
    setSelectedCompanyForTerms('');
    setCompanyTerms('');
    setOriginalTerms('');
    setIsEditingTerms(false);
  };

  return (
    <div className="app-container">
      <div className="main-content">
        {message && <div className="status-message"><p>{message}</p></div>}

        {isSuperAdmin && (
          <div className="company-card">
            <h2 className="company-header">Select Company</h2>
            <div className="company-options">
              {companies.map(c => (
                <label key={c} className="company-label">
                  <input
                    type="radio"
                    name="company"
                    value={c}
                    checked={company === c}
                    onChange={() => setCompany(c)}
                  />
                  <span className="text-gray-700 font-medium">{c}</span>
                </label>
              ))}
              <button
                className="add-company-btn"
                onClick={() => setShowAddCompany(!showAddCompany)}
                title="Add New Company"
              >
                <Plus size={20} />
                <span>Add Company</span>
              </button>
              <button
                className="add-company-btn"
                onClick={handleViewTerms}
                title="View Terms and Conditions"
                disabled={!company}
                style={{ opacity: !company ? 0.6 : 1, cursor: !company ? 'not-allowed' : 'pointer' }}
              >
                <span>View T & C</span>
              </button>
            </div>

            {showAddCompany && (
              <div className="add-company-form">
                <h3 className="add-company-title">Add New Company</h3>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={newCompanyName}
                    onChange={e => setNewCompanyName(e.target.value)}
                    className="form-input"
                    disabled={isCreatingCompany}
                  />
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="Company Description (Optional)"
                    value={newCompanyDescription}
                    onChange={e => setNewCompanyDescription(e.target.value)}
                    className="form-textarea"
                    rows="3"
                    disabled={isCreatingCompany}
                  />
                </div>
                <div className={`form-group logo-upload-box${logoError ? ' logo-error' : ''}`}>
                  <div className="logo-inline-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontWeight: '500' }} htmlFor="logo-file-upload">Logo <span style={{ color: '#f44' }}>*</span>:</label>
                    <button
                      type="button"
                      className="logo-select-btn"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      disabled={isCreatingCompany}
                    >
                      Select Logo
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-file-upload"
                    ref={fileInputRef}
                    onChange={e => setNewCompanyLogo(e.target.files && e.target.files[0])}
                    disabled={isCreatingCompany}
                  />
                  <div className="logo-preview-box" style={{ marginTop: newCompanyLogo ? 14 : 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    {newCompanyLogo && (
                      <>
                        <img draggable={false} src={URL.createObjectURL(newCompanyLogo)} alt="Logo Preview" style={{ height: 48, maxWidth: 90, borderRadius: 8, border: '1.5px solid #e2e8f0', boxShadow: '0 2px 9px rgba(60,60,120,0.13)', objectFit: 'contain', background: '#fff' }} />
                        <div style={{ fontSize: 13, paddingTop: 2, maxWidth: 200, overflow: 'hidden', textAlign: 'center', textWrap: 'nowrap', textOverflow: 'ellipsis' }}>{newCompanyLogo.name}</div>
                        <button
                          type="button"
                          style={{
                            color: '#ef4444',
                            background: 'none',
                            border: 'none',
                            fontSize: 17,
                            cursor: isCreatingCompany ? 'not-allowed' : 'pointer',
                            marginTop: 1,
                            opacity: isCreatingCompany ? 0.5 : 1
                          }}
                          title="Remove"
                          onClick={() => !isCreatingCompany && setNewCompanyLogo(null)}
                          disabled={isCreatingCompany}
                        >
                          ✕
                        </button>
                      </>
                    )}
                    {logoError && (<span className="logo-error-msg">Logo is required</span>)}
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '500' }}>SCM-HEAD (email):</label>
                  <input
                    type="email"
                    placeholder="SCM-HEAD Email"
                    value={newCompanyScmHead}
                    onChange={e => setNewCompanyScmHead(e.target.value)}
                    className="form-input"
                    disabled={isCreatingCompany}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '500' }}>Terms & Conditions</label>
                  <div className="cked-tc-box">
                    <CKEditor
                      editor={ClassicEditor}
                      config={{
                        toolbar: [
                          'bold', 'italic', 'underline', 'strikethrough', 'removeFormat',
                          '|', 'bulletedList', 'numberedList', 'blockQuote', '|', 'link', 'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', 'alignment',
                          '|', 'outdent', 'indent', 'undo', 'redo', 'specialCharacters', 'insertTable',
                        ],
                        // No removePlugins for watermark (branding) - Watermark can't be removed for free build.
                        isReadOnly: isCreatingCompany,
                      }}
                      data={newCompanyTerms}
                      onChange={(event, editor) => !isCreatingCompany && setNewCompanyTerms(editor.getData())}
                    />
                    {/* Watermark note: watermark cannot be removed from CKEditor 5 free build due to license. */}
                  </div>
                </div>
                <div className="form-actions">
                  <button
                    onClick={handleCreateCompany}
                    className="btn-create-company"
                    disabled={isCreatingCompany}
                  >
                    {isCreatingCompany ? 'Creating...' : 'Create Company'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddCompany(false);
                      setNewCompanyName('');
                      setNewCompanyDescription('');
                      setNewCompanyLogo(null);
                      setNewCompanyScmHead('');
                      setNewCompanyTerms('');
                      setLogoError(false);
                    }}
                    className="btn-cancel-company"
                    disabled={isCreatingCompany}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <p className="company-status">
              Designing workflow for:{" "}
              <span className="text-indigo-600 font-semibold">{company}</span>
            </p>
          </div>
        )}

        <div className="workflow-flow">
          {workflow.map((node, index) => (
            <WorkflowNode
              key={index}
              index={index}
              node={node}
              onChange={(updatedNode) => handleNodeChange(index, updatedNode)}
              onRemove={removeNode}
              isLast={index === workflow.length - 1}
              errors={validationErrors[index] || {}}
              disabled={isSavingWorkflow}
            />
          ))}
        </div>

        <div className="actions-container">
          <button
            onClick={handleAddStep}
            className="btn btn-add-step"
            disabled={isSavingWorkflow}
          >
            <Plus size={20} /><span>Add Approval Step</span>
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-submit"
            disabled={isSavingWorkflow}
          >
            {isUpdatingWorkflow ? 'Update Workflow' : 'Submit Final Workflow'}
          </button>
          <button
            onClick={handleReset}
            className="btn btn-reset"
            disabled={isSavingWorkflow}
          >
            Reset Workflow
          </button>
        </div>

        {showConfirm && <ConfirmationModal company={company} onConfirm={handleConfirmSubmit} isUpdate={isUpdatingWorkflow} />}

        {/* Fullscreen Loader for Company Creation */}
        {isCreatingCompany && (
          <div className="fullscreen-loader-overlay">
            <div className="fullscreen-loader-content">
              <FaSpinner className="spinner" style={{ fontSize: '48px', color: '#667eea' }} />
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#333', fontWeight: 500 }}>
                Please Wait, While We Process Your Request
              </p>
            </div>
          </div>
        )}

        {/* Fullscreen Loader for Workflow Save/Update */}
        {isSavingWorkflow && (
          <div className="fullscreen-loader-overlay">
            <div className="fullscreen-loader-content">
              <FaSpinner className="spinner" style={{ fontSize: '48px', color: '#667eea' }} />
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#333', fontWeight: 500 }}>
                Please Wait, While We Process Your Request
              </p>
            </div>
          </div>
        )}

        {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="modal-overlay" onClick={handleCloseTermsModal}>
            <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="terms-modal-header">
                <h2 className="terms-modal-title">{selectedCompanyForTerms} - Terms and Conditions</h2>
                <button
                  className="terms-modal-close-btn"
                  onClick={handleCloseTermsModal}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {isLoadingTerms ? (
                <div className="terms-modal-body">
                  <div className="terms-loading">Loading terms and conditions...</div>
                </div>
              ) : (
                <>
                  <div className="terms-section-header">
                    <h3>Terms and Conditions</h3>
                    {!isEditingTerms && (
                      <button
                        className="btn-edit-terms"
                        onClick={handleEditTerms}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="terms-modal-body">
                    {isEditingTerms ? (
                      <div className="terms-editor-container">
                        <CKEditor
                          editor={ClassicEditor}
                          data={companyTerms}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setCompanyTerms(data);
                          }}
                          config={{
                            toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
                            placeholder: 'Enter terms and conditions...'
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="terms-display"
                        dangerouslySetInnerHTML={{ __html: companyTerms || '<p>No terms and conditions available.</p>' }}
                      />
                    )}
                  </div>

                  {isEditingTerms && (
                    <div className="terms-modal-actions">
                      <button
                        className="btn-terms-save"
                        onClick={handleSaveTerms}
                        disabled={isSavingTerms}
                      >
                        {isSavingTerms ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn-terms-cancel"
                        onClick={handleCancelTerms}
                        disabled={isSavingTerms}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}